import React, { Fragment } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-hot-toast';
import { getNetworkTrafficData, getNetworkTrafficInitials } from '../../redux/actions/networkActions';
import * as XLSX from 'xlsx';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';

import Content from '../../components/Content';
import Table from '../../components/Table';
// import Button from '../../components/Button';
import TextInput from '../../components/TextInput';
import SelectBox from '../../components/SelectBox';
import Pagination from '../../components/Pagination';
import Loader from '../../components/Loader';
import NetworkError from '../../components/Error';
import NetworkTrafficStyles from './NetworkTraffic.module.css';

const FRONTEND_NETWORK_TRAFFIC_ERROR_CODES = {
	INITIAL_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	TABLE_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	FILTER_INVALID: ErrorCodes.API_INVALID_PARAMETER,
	NO_WATCHERS_AVAILABLE: ErrorCodes.RESOURCE_NOT_FOUND,
	CSV_EXPORT_FAILED: ErrorCodes.FILE_PROCESSING_FAILED,
};

const buildNetworkTrafficSupportError = (errorCode, pageInfo, sourceMessage) =>
	formatFrontendSupportError({
		errorCode,
		pageInfo,
		sourceMessage,
	});

const NetworkTrafficScreen = () => {
	const dispatch = useDispatch();
	const [ selectedWatcher, setSelectedWatcher ] = React.useState('');
	const [ selectedTimeframe, setSelectedTimeframe ] = React.useState('');
	const [ searchKey, setSearchKey ] = React.useState('');

	const [ dataLoading, setDataLoading ] = React.useState(false);
	const [ tableDataLoading, setTableDataLoading ] = React.useState(false);
	const [ networkDataInitials, setNetworkDataInitials ] = React.useState({});
	const [ networkData, setNetworkData ] = React.useState({});
	const [ networkTotalRows, setNetworkTotalRows ] = React.useState(0);
	const [ networkRows, setNetworkRows ] = React.useState([]);
	const [ hasError, setHasError ] = React.useState(false);
	const [ supportErrorMessage, setSupportErrorMessage ] = React.useState('');
	const [ copiedSupportError, setCopiedSupportError ] = React.useState(false);

	const [ pageNumber, setPageNumber ] = React.useState(1);
	const pageLimit = 18;

	// Add new state for date range
	const [dateRangeMode, setDateRangeMode] = React.useState('preset'); // 'preset' or 'custom'
	const [startDate, setStartDate] = React.useState('');
	const [endDate, setEndDate] = React.useState('');

	// List of denied countries - this could be fetched from an API instead of hardcoded
	const deniedCountries = [
		'Russia', 'China', 'Iran', 'North Korea', 'Syria', 'Cuba', 'Venezuela',
		'Belarus', 'Myanmar', 'Afghanistan', 'Sudan', 'Somalia', 'Libya',
		'Yemen', 'Iraq', 'Lebanon', 'Palestine', 'Pakistan'
		// Add more denied countries as needed
	];

	// Function to check if a country is denied
	const isDeniedCountry = (country) => {
		if (!country || typeof country !== 'string') return false;
		return deniedCountries.some(deniedCountry => 
			country.toLowerCase().includes(deniedCountry.toLowerCase())
		);
	};

	const setNetworkTrafficSupportError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
		setHasError(true);
		setSupportErrorMessage(buildNetworkTrafficSupportError(errorCode, pageInfo, sourceMessage));
		setCopiedSupportError(false);
	}, []);

	const clearNetworkTrafficSupportError = React.useCallback(() => {
		setHasError(false);
		setSupportErrorMessage('');
		setCopiedSupportError(false);
	}, []);

	const handleCopySupportError = React.useCallback(async () => {
		if (!supportErrorMessage) return;
		try {
			await navigator.clipboard.writeText(supportErrorMessage);
			setCopiedSupportError(true);
			setTimeout(() => setCopiedSupportError(false), 2000);
		} catch (error) {
			setCopiedSupportError(false);
		}
	}, [supportErrorMessage]);

	React.useEffect(
		() => {
			const getData = async () => {
				setDataLoading(true);
				clearNetworkTrafficSupportError();

				try {
					const data = await dispatch(getNetworkTrafficInitials());
					if (data && data.status) {
						setNetworkDataInitials(data);

						const hasWatchers =
							Array.isArray(data.watchers) &&
							data.watchers.length > 0 &&
							Array.isArray(data.watchers[0]) &&
							data.watchers[0].length >= 2;

						if (!hasWatchers) {
							setNetworkTrafficSupportError(
								FRONTEND_NETWORK_TRAFFIC_ERROR_CODES.NO_WATCHERS_AVAILABLE,
								'Network Traffic Page / No Watchers Available',
								'No watchers are configured for network traffic data'
							);
						} else {
							clearNetworkTrafficSupportError();
						}
					} else {
						setNetworkTrafficSupportError(
							FRONTEND_NETWORK_TRAFFIC_ERROR_CODES.INITIAL_LOAD_FAILED,
							'Network Traffic Page / Initial Data Load Failed',
							data?.msg || (typeof data === 'string' ? data : 'Network traffic initial request did not return success')
						);
					}
				} catch (error) {
					setNetworkTrafficSupportError(
						FRONTEND_NETWORK_TRAFFIC_ERROR_CODES.INITIAL_LOAD_FAILED,
						'Network Traffic Page / Initial Data Request Error',
						error?.message || String(error)
					);
				}
				setDataLoading(false);
			};

			getData();
		},
		[ dispatch, clearNetworkTrafficSupportError, setNetworkTrafficSupportError ]
	);

	React.useEffect(
		() => {
			let cancelled = false;
			const getData = async () => {
				if (!selectedWatcher) return;

				setTableDataLoading(true);
				clearNetworkTrafficSupportError();
				setNetworkData({});   // clear the previous watcher's rows immediately on switch

				const formData = new FormData();
				formData.append('watchers', selectedWatcher);
				
				// Handle date range vs preset timeframe
				if (dateRangeMode === 'custom' && startDate && endDate) {
					formData.append('date_range_mode', 'custom');
					formData.append('start_date', startDate); // YYYY-MM-DD format
					formData.append('end_date', endDate);
				} else {
					formData.append('date_range_mode', 'preset');
					formData.append('timeframe', selectedTimeframe);
				}

				const data = await dispatch(getNetworkTrafficData(formData));
				if (cancelled) return;   // a newer watcher/date selection superseded this request
				if (data && data.status) {
					setNetworkData(data);
					clearNetworkTrafficSupportError();
				} else {
					setNetworkData({});
					const isCustomMissingDate =
						dateRangeMode === 'custom' && (!startDate || !endDate);
					setNetworkTrafficSupportError(
						isCustomMissingDate
							? FRONTEND_NETWORK_TRAFFIC_ERROR_CODES.FILTER_INVALID
							: FRONTEND_NETWORK_TRAFFIC_ERROR_CODES.TABLE_LOAD_FAILED,
						isCustomMissingDate
							? 'Network Traffic Page / Custom Date Filter Incomplete'
							: 'Network Traffic Page / Table Data Load Failed',
						data?.msg || 'Network traffic table request did not return success'
					);
				}
				setTableDataLoading(false);

				setPageNumber(1);
			};

			getData();
			return () => { cancelled = true; };
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[dispatch, selectedWatcher, selectedTimeframe, dateRangeMode, startDate, endDate]
	);

	// No auto-selection: the page renders with the dropdown populated but no
	// watcher chosen. The table-loading effect early-returns until the user picks
	// a watcher, so nothing loads on mount.

	React.useEffect(
		() => {
			if (
				networkDataInitials &&
				Object.keys(networkDataInitials).length > 0 &&
				networkDataInitials.timeframe &&
				networkDataInitials.timeframe.length > 0
			) {
				setSelectedTimeframe(networkDataInitials.timeframe[networkDataInitials.timeframe.length - 1]);
			}
		},
		[ networkDataInitials ]
	);

	React.useEffect(
		() => {
			if (networkData && Object.keys(networkData).length > 0 && networkData.NetRows.length > 0) {
				if (searchKey) {
					const netRows = networkData.NetRows.filter((row) => 
						row.some((el) => el && el.toString().toLowerCase().includes(searchKey.toLowerCase()))
					);
					setNetworkTotalRows(netRows.length);
					setNetworkRows(netRows.slice(0, pageLimit));
				} else {
					setNetworkTotalRows(networkData.NetRows.length);
					setNetworkRows(networkData.NetRows.slice(0, pageLimit));
				}
			} else {
				setNetworkTotalRows(0);
				setNetworkRows([]);
			}
		},
		[ networkData, searchKey ]
	);

	const handlePageChange = async (currentPage) => {
		setPageNumber(currentPage.selected + 1);

		const nextContentFrom = currentPage.selected * pageLimit;
		const nextContentTo = currentPage.selected * pageLimit + pageLimit;
		
		if (networkData && Object.keys(networkData).length > 0 && networkData.NetRows.length > 0) {
			let filteredRows = networkData.NetRows;
			
			if (searchKey) {
				// Use the same filtering logic as in useEffect
				filteredRows = networkData.NetRows.filter((row) => 
					row.some((el) => el && el.toString().toLowerCase().includes(searchKey.toLowerCase()))
				);
			}
			
			setNetworkRows(filteredRows.slice(nextContentFrom, nextContentTo));
		}
	};

	// Handle mode toggle
	const handleModeToggle = (mode) => {
		setDateRangeMode(mode);
		if (mode === 'preset') {
			setStartDate('');
			setEndDate('');
		}
	};

	const networkColumns = Array.isArray(networkData?.NetCols) ? networkData.NetCols : [];

	const normalizeNetworkRow = (row, columns = networkColumns) =>
		columns.map((_column, cellIndex) => {
			if (!Array.isArray(row)) return '';
			return row[cellIndex] === null || row[cellIndex] === undefined ? '' : row[cellIndex];
		});

	const getNetworkRowKey = (row, rowIndex) => {
		const absoluteRowIndex = (pageNumber - 1) * pageLimit + rowIndex;
		if (!Array.isArray(row)) return `network-row-${absoluteRowIndex}`;

		return [
			'network-row',
			absoluteRowIndex,
			row[0] || '',
			row[1] || '',
			row[2] || '',
			row[4] || '',
			row[5] || '',
			row[6] || ''
		].join('-');
	};

	const getNetworkCellClassName = (cellIndex, value) => {
		if (cellIndex === 0) {
			return NetworkTrafficStyles.timestampCell;
		}
		if (cellIndex === 1) {
			return NetworkTrafficStyles.watcherIdCell;
		}
		if (cellIndex === 2) {
			return NetworkTrafficStyles.sourceIpCell;
		}
		if (cellIndex === 3) {
			return NetworkTrafficStyles.sourceDeviceCell;
		}
		if (cellIndex === 4) {
			return NetworkTrafficStyles.destinationIpCell;
		}
		if (cellIndex === 7) {
			return isDeniedCountry(value)
				? NetworkTrafficStyles.deniedCountryCell
				: NetworkTrafficStyles.approvedCountryCell;
		}
		return '';
	};

	const exportToCSV = (tableData, columns, filename) => {
		if (!tableData || tableData.length === 0) {
			toast.error('No data to export');
			return;
		}

		try {
			// Create worksheet with headers
			const worksheet = XLSX.utils.aoa_to_sheet([columns]);
			
			// Add data rows
			XLSX.utils.sheet_add_aoa(worksheet, tableData, { origin: 'A2' });
			
			// Create workbook and add the worksheet
			const workbook = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(workbook, worksheet, 'Network Traffic');
			
			// Generate CSV and download
			XLSX.writeFile(workbook, `${filename}.csv`);
			
			toast.success(`Exported to ${filename}.csv`);
		} catch (error) {
			const formattedError = buildNetworkTrafficSupportError(
				FRONTEND_NETWORK_TRAFFIC_ERROR_CODES.CSV_EXPORT_FAILED,
				'Network Traffic Page / CSV Export Failed',
				error?.message || String(error)
			);
			setNetworkTrafficSupportError(
				FRONTEND_NETWORK_TRAFFIC_ERROR_CODES.CSV_EXPORT_FAILED,
				'Network Traffic Page / CSV Export Failed',
				error?.message || String(error)
			);
			toast.error(formattedError);
		}
	};

	return (
		<Content currentMenu='network' headerTitle='Network Traffic'>
			{dataLoading ? (
				<Loader />
			) : (hasError && networkData && Object.keys(networkData).length === 0 && !tableDataLoading) ? (
				<>
					<NetworkError />
					{supportErrorMessage && (
						<div className={NetworkTrafficStyles.supportErrorCard}>
							<strong>Support Error:</strong>
							<code className={NetworkTrafficStyles.supportErrorCode}>{supportErrorMessage}</code>
							<button
								type='button'
								className={NetworkTrafficStyles.copySupportErrorButton}
								onClick={handleCopySupportError}
							>
								{copiedSupportError ? 'Copied' : 'Copy Error Details'}
							</button>
						</div>
					)}
				</>
			) : (
				<Fragment>
					{hasError && supportErrorMessage && (
						<div className={NetworkTrafficStyles.supportErrorBanner}>
							<div className={NetworkTrafficStyles.supportErrorBannerText}>
								<strong>Support Error:</strong>
								<code className={NetworkTrafficStyles.supportErrorCode}>{supportErrorMessage}</code>
							</div>
							<button
								type='button'
								className={NetworkTrafficStyles.copySupportErrorButton}
								onClick={handleCopySupportError}
							>
								{copiedSupportError ? 'Copied' : 'Copy Error Details'}
							</button>
						</div>
					)}
					<div className={NetworkTrafficStyles.filterContainer}>
						<div className={NetworkTrafficStyles.filterContainer_box}>
							<SelectBox
								label='Select Watcher'
								value={selectedWatcher}
								onChange={(e) => setSelectedWatcher(e.target.value)}>
								<option value=''>Select a Watcher</option>
								{networkDataInitials && networkDataInitials.watchers && networkDataInitials.watchers.length === 0 ? (
									<option value='' disabled>No watchers found</option>
								) : (
									networkDataInitials.watchers && networkDataInitials.watchers.map(
										(watcher, key) =>
											watcher.length >= 2 && (
												<option value={watcher[0]} key={watcher + key}>
													{watcher[1]}
												</option>
											)
									)
								)}
							</SelectBox>
						</div>

						{/* Date Range Mode Toggle */}
						<div className={NetworkTrafficStyles.filterContainer_box}>
							<div className={NetworkTrafficStyles.modeToggle}>
								<label>Filter:</label>
								<div className={NetworkTrafficStyles.toggleButtons}>
									<button 
										className={dateRangeMode === 'preset' ? NetworkTrafficStyles.active : ''}
										onClick={() => handleModeToggle('preset')}
									>
										Last 7 Days
									</button>
									<button 
										className={dateRangeMode === 'custom' ? NetworkTrafficStyles.active : ''}
										onClick={() => handleModeToggle('custom')}
									>
										Custom Range
									</button>
								</div>
							</div>
						</div>

						{/* Remove the timeframe dropdown when preset is selected */}
						{/* Custom Date Range Picker - Only show when custom mode is active */}
						{dateRangeMode === 'custom' && (
							<>
								<div className={NetworkTrafficStyles.filterContainer_box}>
									<label className={NetworkTrafficStyles.dateLabel}>Start Date:</label>
									<input
										type='date'
										className={NetworkTrafficStyles.dateInput}
										value={startDate}
										onChange={(e) => setStartDate(e.target.value)}
										max={endDate || new Date().toISOString().split('T')[0]}
									/>
								</div>
								<div className={NetworkTrafficStyles.filterContainer_box}>
									<label className={NetworkTrafficStyles.dateLabel}>End Date:</label>
									<input
										type='date'
										className={NetworkTrafficStyles.dateInput}
										value={endDate}
										onChange={(e) => setEndDate(e.target.value)}
										min={startDate}
										max={new Date().toISOString().split('T')[0]}
									/>
								</div>
							</>
						)}

						<div
							className={`${NetworkTrafficStyles.filterContainer_box} ${NetworkTrafficStyles.searchFilterBox}`}
							style={{ alignItems: 'flex-end' }}
						>
							<TextInput
								label='Search'
								icon='search'
								value={searchKey}
								onChange={(e) => setSearchKey(e.target.value)}
								placeholder='Search here'
								style={{ width: 'min(100%, 30rem)', maxWidth: '100%' }}
							/>
							{/* <Button label='Filter' /> */}
						</div>
					</div>

					{/* Header with Export Buttons */}
					<div className={NetworkTrafficStyles.table_header}>
						<h3 className={NetworkTrafficStyles.table_header_text}>Network Traffic Data</h3>
						<div className={NetworkTrafficStyles.export_buttons}>
							<button
								className={NetworkTrafficStyles.export_button}
								onClick={() => {
									const filteredRows = networkData.NetRows.filter((row) =>
										searchKey
											? row.find((el) => String(el ?? '').toLowerCase().includes(String(searchKey ?? '').toLowerCase()))
											: true
									);
									exportToCSV(
										filteredRows.map((row) => normalizeNetworkRow(row, networkColumns)),
										networkColumns,
										'network_traffic'
									);
								}}
							>
								Export CSV
							</button>
						</div>
					</div>

					{/* Table with Loading Overlay */}
					<div style={{ position: 'relative' }}>
						<Table
							containerStyle={{ height: '60vh' }}
							tableHead={
								<tr>
									{networkColumns.length > 0 &&
										networkColumns.map((col, index) => <th key={`${col}-${index}`}>{col}</th>)}
								</tr>
							}
							tableRow={
								networkTotalRows > 0 && networkRows.length > 0 ? (
									networkRows.map((rows, index) => {
										const rowKey = getNetworkRowKey(rows, index);
										const normalizedRow = normalizeNetworkRow(rows, networkColumns);

										return (
											<tr key={rowKey}>
												{normalizedRow.map((cell, cellIndex) => (
													<td
														key={`${rowKey}-cell-${cellIndex}`}
														className={getNetworkCellClassName(cellIndex, cell)}
													>
														{cell}
													</td>
												))}
											</tr>
										);
									})
								) : tableDataLoading ? (
									// Show empty rows while loading instead of "No data found"
									<tr>
										<td colSpan={networkColumns.length || 1} style={{ textAlign: 'center', height: '200px' }}>
											{/* Empty space while loading */}
										</td>
									</tr>
								) : (
									<tr>
										<td colSpan={networkColumns.length || 1} style={{ textAlign: 'center' }}>
											No data found
										</td>
									</tr>
								)
							}
						/>
						
						{/* Loading Overlay */}
						{tableDataLoading && (
							<div style={{
								position: 'absolute',
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								backgroundColor: 'rgba(0, 0, 0, 0.3)',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								zIndex: 10
							}}>
								<div className={NetworkTrafficStyles.spinner} />
							</div>
						)}
					</div>

					{!tableDataLoading && (
						<div className='paginationContainer'>
							<Pagination
								onPageChange={handlePageChange}
								rowsPerPage={pageLimit}
								totalPageSize={networkTotalRows}
								pageNumber={pageNumber}
							/>
						</div>
					)}
				</Fragment>
			)}
		</Content>
	);
};

export default NetworkTrafficScreen;
